import React from "react";
import { MaterialDesignContent } from "notistack";
import { styled } from "@mui/material/styles";

/* Notifications in the design system's shape - the header band's 12px radius,
   hairline and type, ink text - with the product's familiar green and red kept
   as a tint: each kind of message sets a ground, an edge and a bar down its
   leading side, quieter than the solid fills it had before. The toasts portal
   to <body>, outside the app's own .orion-tokens, so each carries the class
   itself; the light theme's token overrides then apply to it as well. */
const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent': {
    '--toast-bg': 'var(--glass)',
    '--toast-edge': 'var(--line-strong)',
    '--toast-bar': 'var(--tone-done)',
    background: 'var(--toast-bg)',
    backdropFilter: 'blur(var(--glass-blur))',
    WebkitBackdropFilter: 'blur(var(--glass-blur))',
    border: '1px solid var(--toast-edge)',
    borderRadius: 12,
    boxShadow: 'inset 3px 0 0 var(--toast-bar), 0 12px 32px rgba(0, 0, 0, 0.28)',
    color: 'var(--text-90)',
    fontFamily: 'var(--font-sans)',
    fontSize: 13,
    lineHeight: 1.45,
    padding: '10px 10px 10px 18px',
    /* wide enough that an error's hint reads as one line on a desktop */
    maxWidth: 'min(900px, calc(100vw - 48px))',
    /* notistack wraps the close button onto its own line once the message is
       long; keep it on the first line, top right, beside the text */
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
  },
  '&.notistack-MuiContent-info, &.notistack-MuiContent-success': {
    '--toast-bg': 'rgba(30, 44, 16, 0.92)',
    '--toast-edge': 'rgba(167, 192, 138, 0.28)',
    '--toast-bar': '#8fb26a',
  },
  '&.notistack-MuiContent-error': {
    '--toast-bg': 'rgba(60, 17, 13, 0.92)',
    '--toast-edge': 'rgba(254, 163, 157, 0.28)',
    '--toast-bar': '#f0564a',
  },
  'body[data-theme="light"] &.notistack-MuiContent-info, body[data-theme="light"] &.notistack-MuiContent-success': {
    '--toast-bg': 'rgba(233, 241, 222, 0.96)',
    '--toast-edge': 'rgba(84, 116, 48, 0.3)',
    '--toast-bar': '#5f8a33',
  },
  'body[data-theme="light"] &.notistack-MuiContent-error': {
    '--toast-bg': 'rgba(252, 231, 227, 0.96)',
    '--toast-edge': 'rgba(190, 50, 40, 0.3)',
    '--toast-bar': '#c8392d',
  },
  '& > #notistack-snackbar': {
    flex: '1 1 auto',
    minWidth: 0,
    padding: '4px 0',
  },
  '& > :not(#notistack-snackbar)': {
    alignSelf: 'flex-start',
    marginRight: 0,
    paddingLeft: 12,
  },
  /* the close buttons are passed in with an inline white colour, which would
     vanish on the light theme's cream */
  '& .MuiButton-root': {
    minWidth: 28,
    width: 28,
    height: 28,
    padding: 0,
    borderRadius: 8,
    color: 'var(--text-55) !important',
  },
  '& .MuiButton-root:hover': {
    background: 'rgba(var(--ink), 0.08)',
    color: 'var(--text) !important',
  },
  '& .MuiButton-root svg': {
    width: 18,
    height: 18,
  },
  '& .af-toast__title': {
    color: 'var(--text)',
    fontWeight: 500,
  },
  '& .af-toast__hint': {
    color: 'var(--text-55)',
    marginTop: 4,
  },
}));

const DesignSystemToast = React.forwardRef((props, ref) => (
  <StyledMaterialDesignContent
    ref={ref}
    {...props}
    className={`orion-tokens${props.className ? ` ${props.className}` : ''}`}
  />
));
DesignSystemToast.displayName = 'DesignSystemToast';

export default DesignSystemToast;
